/**
 * Общая оболочка экранов входа/регистрации/восстановления и онбординга (v2):
 * тёмно-бирюзовая панель слева (на телефоне — шапка), форма справа.
 * Здесь же — поле пароля с кнопкой «Показать» и индикатор надёжности пароля.
 */
import React, { useState } from 'react';
import { Icon, BrandMark } from './ui';
import './Login.css';

const c = (token) => ({ fill: `var(--ds-${token})` });
const s = (token) => ({ stroke: `var(--ds-${token})` });
const TEXT = { fontFamily: 'Inter, sans-serif' };
const MONO = { fontFamily: 'ui-monospace, Menlo, monospace' };

/** Знак бренда: на тёмно-бирюзовой панели — без плашки, на светлом фоне — на плашке. */
export const Logo = ({ size = 34, variant = 'tile' }) => <BrandMark size={size} variant={variant} />;

/* ---------- Иллюстрации панели (перерисованы из макетов на токенах) ---------- */

const IllCards = () => (
  <svg width="344" height="230" viewBox="0 0 344 230" role="img" aria-label="Карточки со словами и кольцо прогресса">
    <circle cx="292" cy="40" r="34" style={c('primary-hover')} />
    <circle cx="30" cy="200" r="22" style={c('primary-hover')} />
    <rect x="40" y="42" width="220" height="140" rx="16" style={c('primary')} transform="rotate(-8 150 112)" />
    <rect x="56" y="34" width="220" height="140" rx="16" style={c('primary-border')} transform="rotate(-3 166 104)" />
    <rect x="70" y="30" width="220" height="140" rx="16" style={c('surface')} />
    <rect x="90" y="52" width="64" height="20" rx="10" style={c('primary-muted')} />
    <text x="122" y="66" textAnchor="middle" fontSize="11" fontWeight="600" style={{ ...c('primary'), ...TEXT }}>Изучаю</text>
    <text x="90" y="106" fontSize="24" fontWeight="700" style={{ ...c('text'), ...TEXT }}>serendipity</text>
    <text x="90" y="130" fontSize="13" style={{ ...c('text-muted'), ...TEXT }}>счастливая случайность</text>
    <rect x="90" y="146" width="36" height="6" rx="3" style={c('primary')} />
    <rect x="130" y="146" width="36" height="6" rx="3" style={c('primary')} />
    <rect x="170" y="146" width="36" height="6" rx="3" style={c('border-subtle')} />
    <rect x="210" y="146" width="36" height="6" rx="3" style={c('border-subtle')} />
    <circle cx="292" cy="176" r="30" style={{ ...c('primary-dark'), ...s('primary-hover') }} strokeWidth="8" />
    <circle cx="292" cy="176" r="30" fill="none" style={s('primary-bright')} strokeWidth="8" strokeLinecap="round" strokeDasharray="132 189" transform="rotate(-90 292 176)" />
    <text x="292" y="181" textAnchor="middle" fontSize="14" fontWeight="700" style={{ ...c('on-primary'), ...TEXT }}>70%</text>
  </svg>
);

const IllProfile = ({ initial = 'А' }) => (
  <svg width="344" height="210" viewBox="0 0 344 210" role="img" aria-label="Профиль и значок достижения">
    <circle cx="286" cy="44" r="28" style={c('primary-hover')} />
    <rect x="40" y="40" width="200" height="140" rx="16" style={c('surface')} />
    <circle cx="84" cy="88" r="24" style={c('primary-border')} />
    <text x="84" y="95" textAnchor="middle" fontSize="18" fontWeight="700" style={{ ...c('primary-dark'), ...TEXT }}>{initial}</text>
    <rect x="120" y="74" width="96" height="12" rx="6" style={c('text')} />
    <rect x="120" y="94" width="64" height="10" rx="5" style={c('border')} />
    <rect x="62" y="132" width="156" height="10" rx="5" style={c('border-subtle')} />
    <rect x="62" y="132" width="40" height="10" rx="5" style={c('primary')} />
    <text x="62" y="164" fontSize="12" style={{ ...c('text-muted'), ...TEXT }}>Уровень 1 · 0 XP</text>
    <circle cx="270" cy="140" r="34" style={c('primary')} />
    <path d="M255 140l10 10 20-22" fill="none" style={s('on-primary')} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IllLock = () => (
  <svg width="344" height="210" viewBox="0 0 344 210" role="img" aria-label="Замок и конверт">
    <circle cx="60" cy="50" r="26" style={c('primary-hover')} />
    <circle cx="300" cy="170" r="30" style={c('primary-hover')} />
    <rect x="70" y="70" width="190" height="124" rx="14" style={c('primary-border')} />
    <path d="M70 84l95 62 95-62" fill="none" style={s('primary')} strokeWidth="6" strokeLinejoin="round" />
    <rect x="198" y="34" width="96" height="84" rx="16" style={c('surface')} />
    <path d="M222 34v-8a24 24 0 0 1 48 0v8" fill="none" style={s('surface')} strokeWidth="10" />
    <circle cx="246" cy="70" r="9" style={c('primary-dark')} />
    <rect x="242" y="74" width="8" height="22" rx="4" style={c('primary-dark')} />
  </svg>
);

const IllKey = () => (
  <svg width="344" height="170" viewBox="0 0 344 170" role="img" aria-label="Ключ и защищённое хранилище">
    <circle cx="300" cy="36" r="24" style={c('primary-hover')} />
    <rect x="40" y="30" width="230" height="110" rx="16" style={c('surface')} />
    <text x="62" y="66" fontSize="12" style={{ ...c('text-muted'), ...TEXT }}>OpenAI API key</text>
    <rect x="62" y="80" width="186" height="34" rx="8" style={c('surface-muted')} />
    <text x="74" y="102" fontSize="14" style={{ ...c('text'), ...MONO }}>sk-••••••••••4f2a</text>
    <path d="M270 92l40-14v34c0 22-16 34-40 42-24-8-40-20-40-42V78z" style={{ ...c('primary'), ...s('primary-dark') }} strokeWidth="4" />
    <path d="M258 108l9 9 17-19" fill="none" style={s('on-primary')} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IllPodium = () => (
  <svg width="344" height="200" viewBox="0 0 344 200" role="img" aria-label="Пьедестал недельного рейтинга">
    <circle cx="296" cy="36" r="24" style={c('primary-hover')} />
    <circle cx="172" cy="40" r="22" style={c('primary-border')} />
    <circle cx="92" cy="80" r="18" style={c('surface')} />
    <circle cx="252" cy="96" r="18" style={c('surface')} />
    <rect x="132" y="72" width="80" height="120" rx="10" style={c('primary')} />
    <text x="172" y="112" textAnchor="middle" fontSize="26" fontWeight="700" style={{ ...c('on-primary'), ...TEXT }}>1</text>
    <rect x="52" y="108" width="80" height="84" rx="10" style={c('primary-border')} />
    <text x="92" y="146" textAnchor="middle" fontSize="22" fontWeight="700" style={{ ...c('primary-dark'), ...TEXT }}>2</text>
    <rect x="212" y="124" width="80" height="68" rx="10" style={c('primary-hover')} />
    <text x="252" y="160" textAnchor="middle" fontSize="22" fontWeight="700" style={{ ...c('primary-pale'), ...TEXT }}>3</text>
  </svg>
);

const ILLUSTRATIONS = { cards: IllCards, profile: IllProfile, lock: IllLock, key: IllKey, podium: IllPodium };

/**
 * Боковая панель. aside = { art, title, lead?, points[], footer? }.
 * mobileTitle — короткий заголовок для шапки на телефоне.
 */
export const AuthAside = ({ art = 'cards', title, lead, points = [], footer = 'english.evolvenext.net', initial }) => {
  const Art = ILLUSTRATIONS[art] || IllCards;
  return (
    <aside className="auth-aside">
      <div className="auth-brand"><Logo variant="plain" /><span>English Learning</span></div>
      <div className="auth-aside-art"><Art initial={initial} /></div>
      <div className="auth-aside-copy">
        <div className="auth-aside-title">{title}</div>
        {lead && <div className="auth-aside-lead">{lead}</div>}
        {points.length > 0 && (
          <ul className="auth-aside-points">
            {points.map((p) => (
              <li key={p}><Icon name="check" size={18} strokeWidth={2.4} /><span>{p}</span></li>
            ))}
          </ul>
        )}
      </div>
      <div className="auth-aside-footer">{footer}</div>
    </aside>
  );
};

/** Экран входа/регистрации: панель + центрированная колонка с формой. */
export const AuthLayout = ({ aside, mobileTitle, children, width = 400 }) => (
  <div className="auth">
    <AuthAside {...aside} />
    <header className="auth-mobile-head">
      <div className="auth-brand"><Logo variant="plain" /><span>English Learning</span></div>
      <div className="auth-mobile-title">{mobileTitle || aside.title}</div>
    </header>
    <main className="auth-main">
      <div className="auth-col" style={{ '--auth-col-w': `${width}px` }}>{children}</div>
    </main>
  </div>
);

export const AuthHeading = ({ title, children }) => (
  <div className="auth-heading">
    <h1>{title}</h1>
    {children && <div className="auth-sub">{children}</div>}
  </div>
);

export const AuthBadge = ({ icon }) => (
  <div className="auth-badge" aria-hidden="true"><Icon name={icon} size={28} strokeWidth={2} /></div>
);

/**
 * Поле пароля с текстовой кнопкой «Показать/Скрыть» (доступна с клавиатуры).
 * Пропсы id/aria-* приходят от <Field> из UI-кита.
 */
export const PasswordInput = React.forwardRef(({ className = '', invalid, ...rest }, ref) => {
  const [shown, setShown] = useState(false);
  const isInvalid = invalid || rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';
  return (
    <div className={`auth-pass ${isInvalid ? 'is-invalid' : ''} ${className}`}>
      <input ref={ref} type={shown ? 'text' : 'password'} {...rest} />
      <button
        type="button"
        className="auth-pass-toggle"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
        aria-pressed={shown}
        title={shown ? 'Скрыть пароль' : 'Показать пароль'}
      >
        <Icon name="eye" size={20} strokeWidth={2} />
        {shown && <span className="auth-pass-slash" aria-hidden="true" />}
      </button>
    </div>
  );
});

/* ---------- Надёжность пароля ---------- */

export const PASSWORD_MIN = 6; // минимум, который проверяет бэкенд (register / reset-password)

const RULES = [
  { id: 'len', label: 'Не короче 8 символов', test: (p) => p.length >= 8 },
  { id: 'mix', label: 'Буквы и цифры', test: (p) => /\p{L}/u.test(p) && /\d/.test(p) },
  { id: 'upper', label: 'Заглавная буква', test: (p) => /\p{Lu}/u.test(p) },
  { id: 'special', label: 'Спецсимвол (!, ?, # …)', test: (p) => /[^\p{L}\d\s]/u.test(p) }
];
const LEVELS = ['Слабый', 'Слабый', 'Средний', 'Хороший', 'Надёжный'];

export const scorePassword = (p = '') => RULES.reduce((n, r) => n + (r.test(p) ? 1 : 0), 0);

/** Полоса из 4 сегментов + чек-лист рекомендаций. */
export const PasswordStrength = ({ password = '', id }) => {
  const score = password ? scorePassword(password) : 0;
  const tone = !password ? '' : score <= 1 ? 'is-weak' : score === 2 ? 'is-medium' : 'is-good';
  return (
    <div className={`auth-strength ${tone}`} id={id}>
      <div className="auth-strength-row">
        <div className="auth-strength-bar" role="meter" aria-label="Надёжность пароля" aria-valuemin={0} aria-valuemax={4} aria-valuenow={score} aria-valuetext={password ? LEVELS[score] : 'Пароль не введён'}>
          {[0, 1, 2, 3].map((i) => <span key={i} className={i < score ? 'is-on' : ''} />)}
        </div>
        <span className="auth-strength-label">{password ? LEVELS[score] : `Минимум ${PASSWORD_MIN} символов`}</span>
      </div>
      <ul className="auth-strength-rules">
        {RULES.map((r) => {
          const ok = !!password && r.test(password);
          return (
            <li key={r.id} className={ok ? 'is-ok' : ''}>
              {ok ? <Icon name="check" size={14} strokeWidth={2.6} /> : <span className="auth-strength-dot" aria-hidden="true"><span /></span>}
              {r.label}
              <span className="visually-hidden">{ok ? ' — выполнено' : ' — не выполнено'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Сообщение под полем (ошибка или успех), в стиле Field из UI-кита. */
export const FieldNote = ({ id, tone = 'error', children }) => (
  <span id={id} className={tone === 'error' ? 'ui-field-error' : 'auth-field-ok'}>
    <Icon name={tone === 'error' ? 'alert' : 'check'} size={14} strokeWidth={2.4} />{children}
  </span>
);

/**
 * Текст ошибки для пользователя: технические детали сервера (переменные окружения,
 * пути к файлам, порты) не показываем — только общий текст.
 */
const TECH_RE = /MAIL_|SMTP|\.env|\.md\b|backend|Firestore|GOOGLE_|USE_FIRESTORE|порт \d|port \d|ECONN|ETIMEDOUT|stack|uv_interface/i;
export const userMessage = (error, fallback) => {
  const status = error?.response?.status;
  const msg = error?.response?.data?.message;
  if (!error?.response) return 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';
  if (status >= 500) return fallback;
  if (typeof msg === 'string' && msg.trim() && !TECH_RE.test(msg)) return msg.trim();
  return fallback;
};

export const cleanMessage = (msg, fallback) =>
  (typeof msg === 'string' && msg.trim() && !TECH_RE.test(msg) ? msg.trim() : fallback);

/**
 * Флаг «только что зарегистрировался — показать онбординг». После регистрации маршрут /register
 * сразу перерисовывается в <Navigate> (пользователь уже авторизован), поэтому навигацию
 * из самой формы React Router игнорирует — App.js смотрит на этот флаг.
 */
const ONB_KEY = 'onboardingPending';
export const markOnboardingPending = (name) => {
  try { sessionStorage.setItem(ONB_KEY, JSON.stringify({ name: name || null })); } catch (_) { /* приватный режим */ }
};
export const readOnboardingPending = () => {
  try { return JSON.parse(sessionStorage.getItem(ONB_KEY) || 'null'); } catch (_) { return null; }
};
export const clearOnboardingPending = () => {
  try { sessionStorage.removeItem(ONB_KEY); } catch (_) { /* ничего */ }
};
